import {Injectable, Optional} from '@angular/core';
import * as _ from 'lodash-es';
import {forkJoin, Observable, of} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {Exchange, EXCHANGE_NAMING} from '../../enums/exchange';
import {QueryLangFilterOperator, QueryLangFilterOperatorExtension} from '../../enums/query-lang-filter-operator';
import {SortDirection} from '../../enums/sort-direction';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {FilterCriterion} from '../../models/filters/filter-criterion';
import {QueryLangFilterCriterion} from '../../models/filters/query-lang-filter-criterion';
import {Security} from '../../models/security';
import {formatSecurityKey} from '../../models/security-key';
import {AppConfigService} from '../../services/config.service';
import {AutocompleteProvider, AutocompleteProviderConfig} from '../autocomplete';
import {SecurityService} from '../security';
import {AspreaderSecurityVersionService} from '../security/aspreader-security';
import {ExchangesService} from '../security/exchanges/exchanges.service';
import {
    SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
    SECURITY_SYMBOL_FILTER_FIELD_NAMES
} from '../security/security-filter-criterion-utils';
import {SmartSecurity, SmartSecurityService} from '../smart-security';

const SECURITY_AUTOCOMPLETE_MAX_COUNT = 10;
export const SECURITY_AUTOCOMPLETE_SORT_FIELD: keyof Security = 'symbol';

export enum SecurityAutocompleteFieldKey {
    SYMBOL   = 'SYMBOL',
    EXCHANGE = 'EXCHANGE'
}

export type SecuritySearchVariant = Partial<{[key in SecurityAutocompleteFieldKey]: string}>;

export interface SecurityAutocompleteItem {
    security: Security;
    searchTerms: SecuritySearchVariant;
}

export const SECURITY_KEY_TO_FIELD_NAME_DICT: { [key in SecurityAutocompleteFieldKey]: keyof Security } = {
    [SecurityAutocompleteFieldKey.SYMBOL]:   'symbol',
    [SecurityAutocompleteFieldKey.EXCHANGE]: 'securityExchange'
};

export interface SecuritySearchVariantFilter {
    searchVariant: SecuritySearchVariant;
    filters: FilterCriterion[];
    isSmart?: boolean;
    isAspreader?: boolean;
    aspreaderNameSubstr?: string;
}

@Injectable()
export class SecurityAutocompleteService implements AutocompleteProvider<SecurityAutocompleteItem> {

    /**
     * Matches two strings by splitting them into words, and searching using these words.
     */
    public static isSubStrIncludedInStrByWords(mainStr: string, subStr: string): boolean {
        const subStrEscapedWords = subStr
            .split(/\s+/)
            .map(_.escapeRegExp);
        const subStrRegExp = new RegExp(`^${subStrEscapedWords.join('.*\\s')}`, 'i');

        return subStrRegExp.test(mainStr);
    }

    /**
     * Create an array of all permutations for the given array of options.
     * https://github.com/trekhleb/javascript-algorithms/tree/master/src/algorithms/sets/permutations
     * @param {[]} permutationOptions
     */
    private static permuteWithoutRepetitions<T>(permutationOptions: T[]): T[][] {
        if (permutationOptions.length === 1) {
            return [permutationOptions];
        }

        // Init permutations array.
        const permutations = [];

        // Get all permutations for permutationOptions excluding the first element.
        const smallerPermutations = this.permuteWithoutRepetitions(permutationOptions.slice(1));

        // Insert first option into every possible position of every smaller permutation.
        const firstOption = permutationOptions[0];

        for (const smallerPermutation of smallerPermutations) {

            // Insert first option into every possible position of smallerPermutation.
            for (let positionIndex = 0; positionIndex <= smallerPermutation.length; positionIndex += 1) {
                const permutationPrefix = smallerPermutation.slice(0, positionIndex);
                const permutationSuffix = smallerPermutation.slice(positionIndex);
                permutations.push(permutationPrefix.concat([firstOption], permutationSuffix));
            }
        }

        return permutations;
    }

    /**
     * Returns all possible variants of term-parts.
     *
     * @example:
     *   getTermPartsVariants(['a', 'b', 'c'], 2)
     *   returns [[['a'], ['b', 'c']], [['a', 'b'], ['c']], [['a', 'b', 'c']]]
     *
     *   getTermPartsVariants(['a', 'b'], 3)
     *   returns [[['a'], ['b']], [['a', 'b']]]
     *
     *   getTermPartsVariants(['a', 'b'], 1)
     *   returns [[['a', 'b']]]
     *
     * @param {string[]} termWords array of words in the term
     * @param {number} termPartCount count of parts in which the term should be split
     * @returns {string[][][]}
     */
    private static getTermPartsVariants(termWords: string[], termPartCount: number): string[][][] {
        const getVariantsByFirstPartLength = (firstPartLength: number): string[][][] => {
            const firstPart: string[] = termWords.slice(0, firstPartLength);
            const restPartsVariants: string[][][] = (firstPartLength >= termWords.length || termPartCount <= 1)
                ? []
                : SecurityAutocompleteService.getTermPartsVariants(
                    termWords.slice(firstPartLength),
                    termPartCount - 1
                );

            return restPartsVariants.length
                ? restPartsVariants.map(restPartVariant => [
                    firstPart,
                    ...restPartVariant
                ])
                : firstPart.length
                    ? [[firstPart]]
                    : [];
        };

        const variants: string[][][] = [];
        const maxPartLength = termWords.length;
        const minPartLength = termPartCount === 1 ? Math.max(termWords.length, 1) : 1;

        if (maxPartLength) {
            for (let firstPartLength = minPartLength; firstPartLength <= maxPartLength; firstPartLength++) {
                variants.push(
                    ...getVariantsByFirstPartLength(firstPartLength)
                );
            }
        } else {
            variants.push(
                ...getVariantsByFirstPartLength(1)
            );
        }

        return variants;
    }

    private readonly securityFieldKeys = this.getSecurityFieldKeys();

    private readonly allKeyVariants = SecurityAutocompleteService.permuteWithoutRepetitions(this.securityFieldKeys);

    private readonly isSmartSecurityEnabled = !!this.appConfigService.appConfig.isSmartSecurityEnabled;

    constructor(

        // [HED-930] It's not SmartSecurityService here, since it requires accounts
        protected securityService: SecurityService,

        protected appConfigService: AppConfigService,
        protected exchangesService: ExchangesService,

        @Optional()
        protected aspreaderSecurityVersionService: AspreaderSecurityVersionService
    ) {}

    public autocomplete(
        term: string,
        config?: AutocompleteProviderConfig<SecurityAutocompleteItem>
    ): Observable<SecurityAutocompleteItem[]> {
        const searchVariants = this.getSearchVariants(term);

        let inputFilters: FilterCriterion[] = config && config.filters;
        const smartExchangeExclusionFilterIndex = inputFilters
            ? this.findSmartExchangeExclusionFilterIndex(inputFilters)
            : -1;
        const doIncludeSmart: boolean = this.isSmartSecurityEnabled && smartExchangeExclusionFilterIndex < 0;

        if (smartExchangeExclusionFilterIndex >= 0) {
            inputFilters = [...inputFilters];
            inputFilters.splice(smartExchangeExclusionFilterIndex, 1);
        }

        const filtersVariantsFactories: Array<Observable<SecuritySearchVariantFilter[]>> = [
            ...(doIncludeSmart
                ? [this.createSmartSecurityFiltersVariants(searchVariants)]
                : [of([])]
            ),
            this.createNoSmartSecurityFiltersVariants(searchVariants),
            this.createExchangeNamingFiltersVariants(searchVariants),
            this.createAsecurityFiltersVariants(searchVariants)
        ];

        const finalFiltersVariants$: Observable<SecuritySearchVariantFilter[]> =
                  forkJoin(filtersVariantsFactories).pipe(
                      map(groups => _.flatten(groups))
                  );

        const createRequests = (filtersVariantsWithSmarts: SecuritySearchVariantFilter[]) =>
            filtersVariantsWithSmarts
                .map<Observable<SecurityAutocompleteItem[]>>(filtersVariant => {
                    const createSecurityServiceRequest = (): Observable<Security[]> => {
                        const filterCriteria: FilterCriterion[] = inputFilters && inputFilters.length
                            ? [...inputFilters, ...filtersVariant.filters]
                            : filtersVariant.filters;

                        const filter: CommonListFilter = {
                            filters:       filterCriteria,
                            pageIndex:     0,
                            pageSize:      100, // TODO: SECURITY_AUTOCOMPLETE_MAX_COUNT ?
                            sortColumn:    SECURITY_AUTOCOMPLETE_SORT_FIELD,
                            sortDirection: SortDirection.ASC
                        };

                        return this.securityService.getSecurities(filter).pipe(
                            switchMap(response =>
                                this.prepareFoundSecurities(filtersVariant, response.data, doIncludeSmart)
                            )
                        );
                    };

                    const createAsecurityRequest = () =>
                        this.aspreaderSecurityVersionService.getFilteredLastVersionSecurities(
                            filtersVariant.aspreaderNameSubstr
                        );

                    const request$: Observable<Security[]> = (
                        filtersVariant.isAspreader && this.aspreaderSecurityVersionService
                    )
                        ? createAsecurityRequest()
                        : createSecurityServiceRequest();

                    return request$.pipe(
                        map(securities => securities
                            .map<SecurityAutocompleteItem>(security => ({
                                security,
                                searchTerms: filtersVariant.searchVariant
                            }))
                        )
                    );
                });

        return finalFiltersVariants$.pipe(
            switchMap(finalFiltersVariants => {
                const requests = finalFiltersVariants.length
                    ? createRequests(finalFiltersVariants)
                    : [of([])]; // [TRAD-196] Emit empty list, when no variants.

                return forkJoin(requests).pipe(
                    map(listVariants => this.unionAutocompleteVariants(listVariants)),
                    map(autocompleteItems => this.reduceAutocompleteVariants(autocompleteItems)),
                    tap(autocompleteItems => this.actualizeSecurities(autocompleteItems))
                );
            })
        );
    }

    public getSearchVariants(term: string): SecuritySearchVariant[] {
        const termWords: string[] = term
                .split(/\s+/)
                .map(termWord => termWord.trim())
                .filter(termWord => termWord.length);

        let result: SecuritySearchVariant[];

        if (termWords.length) {
            const termPartsVariants = SecurityAutocompleteService.getTermPartsVariants(
                termWords,
                this.securityFieldKeys.length
            );

            result = this.allKeyVariants.reduce<SecuritySearchVariant[]>(
                (variantsAcc, keys) => {
                    termPartsVariants.forEach(termParts => {
                        const searchVariant: SecuritySearchVariant = keys.reduce<SecuritySearchVariant>(
                            (variantAcc, key, keyIndex) => {
                                if (keyIndex < termParts.length) {
                                    variantAcc = variantAcc || {};
                                    variantAcc[key] = termParts[keyIndex].join(' ');
                                }

                                return variantAcc;
                            },
                            undefined
                        );

                        if (searchVariant) {
                            variantsAcc.push(searchVariant);
                        }
                    });

                    return variantsAcc;
                },
                []
            );
        } else {
            result = [
                {
                    [SecurityAutocompleteFieldKey.SYMBOL]: ''
                }
            ];
        }

        return result;
    }

    protected unionAutocompleteVariants(listVariants: SecurityAutocompleteItem[][]): SecurityAutocompleteItem[] {
        return _.unionBy(
            [],
            ...listVariants as any[],
            (item: SecurityAutocompleteItem) => formatSecurityKey(item.security)
        );
    }

    protected reduceAutocompleteVariants(autocompleteItems: SecurityAutocompleteItem[]): SecurityAutocompleteItem[] {
        let reducedList: SecurityAutocompleteItem[];

        if (autocompleteItems.length > SECURITY_AUTOCOMPLETE_MAX_COUNT) {
            const listVariants = autocompleteItems
                .reduce<SecurityAutocompleteItem[][]>((currentGroups, autocompleteItem) => {
                    const isNewGroup = !currentGroups.length
                        || currentGroups[currentGroups.length - 1][0].searchTerms !== autocompleteItem.searchTerms;

                    if (isNewGroup) {
                        currentGroups.push([autocompleteItem]);
                    } else {
                        currentGroups[currentGroups.length - 1].push(autocompleteItem);
                    }

                    return currentGroups;
                }, []);

            do {
                const largestGroup = _.maxBy(
                    listVariants,
                    (listVariant: SecurityAutocompleteItem[]) => listVariant.length
                );

                largestGroup.pop();

                reducedList = [].concat(...listVariants);
            } while (reducedList.length > SECURITY_AUTOCOMPLETE_MAX_COUNT);
        }

        return reducedList || autocompleteItems;
    }

    /**
     * Updates each security in the given array to the latest stored in cache.
     * @param {SecurityAutocompleteItem} autocompleteItems
     */
    protected actualizeSecurities(autocompleteItems: SecurityAutocompleteItem[]): void {
        return autocompleteItems.forEach(item =>
            item.security = this.securityService.getSecurityFromCacheBySecurityKey(item.security)
                || item.security // Useful for Smart securities.
        );
    }

    protected createNoSmartSecurityFiltersVariants(
        searchVariants: SecuritySearchVariant[]
    ): Observable<SecuritySearchVariantFilter[]> {
        type SearchVariantPredicate = (searchVariant: SecuritySearchVariant) => boolean;

        const isExchangeVariantAcceptable: SearchVariantPredicate = (this.exchangesService.isExchangesApiAvailable())
            ? searchVariant => !searchVariant[SecurityAutocompleteFieldKey.EXCHANGE]
            : searchVariant => this.isVariantWithoutSpaces(searchVariant, SecurityAutocompleteFieldKey.EXCHANGE);

        const filtersVariantsNoSmarts: SecuritySearchVariantFilter[] = searchVariants

            /**
             * Exclude filters, where symbol or exchange should contain spaces (e.g. "btc usd"),
             * because it's impossible for common symbols and exchanges in API (e.g. "BTC/USD", BINANCE_FUT).
             * Also exclude exchange searches, when exchanges API is available,
             * since in that case UI knows all exchanges and can find exchange by its own.
             */
            .filter(searchVariant =>
                this.isVariantWithoutSpaces(searchVariant, SecurityAutocompleteFieldKey.SYMBOL)
                && isExchangeVariantAcceptable(searchVariant)
            )

            .map<SecuritySearchVariantFilter>(searchVariant => ({
                    searchVariant,
                    filters: (Object.keys(searchVariant) as SecurityAutocompleteFieldKey[])
                                 .map<QueryLangFilterCriterion>(key =>
                                     new QueryLangFilterCriterion({
                                         fieldNames: [SECURITY_KEY_TO_FIELD_NAME_DICT[key]],
                                         operator:   QueryLangFilterOperatorExtension.STARTS_WITH,
                                         query:      searchVariant[key]
                                     })
                                 )
                })
            );

        return of(filtersVariantsNoSmarts);
    }

    protected createSmartSecurityFiltersVariants(
        searchVariants: SecuritySearchVariant[]
    ): Observable<SecuritySearchVariantFilter[]> {
        let result$: Observable<SecuritySearchVariantFilter[]>;

        const smartExchangeName = EXCHANGE_NAMING[Exchange.SMART];

        const smartSecuritySearchVariants = searchVariants
            .filter(filtersVariant =>

                    /**
                     * Exclude filters, where symbol should contain spaces (e.g. "b u"),
                     * because it's impossible for SMART symbols in API (e.g. BTC/USD Smart).
                     */
                    this.isVariantWithoutSpaces(filtersVariant, SecurityAutocompleteFieldKey.SYMBOL)

                    && this.isVariantMatchesMainStr(
                        filtersVariant,
                        SecurityAutocompleteFieldKey.EXCHANGE,
                        smartExchangeName
                    )
            );

        if (smartSecuritySearchVariants.length) {
            result$ = (this.securityService as SmartSecurityService).getSmartBoundExistingAccountsExchanges().pipe(
                map(accountsExchanges => {
                    let filtersVariants: SecuritySearchVariantFilter[];

                    if (accountsExchanges.length) {
                        const accountExchangesFilter = new QueryLangFilterCriterion({
                            fieldNames: [SECURITY_KEY_TO_FIELD_NAME_DICT[SecurityAutocompleteFieldKey.EXCHANGE]],
                            operator:   QueryLangFilterOperator.IN,
                            query:      accountsExchanges.join(',')
                        });

                        filtersVariants = smartSecuritySearchVariants
                            .map<SecuritySearchVariantFilter>((searchVariant, variantIndex) => ({
                                searchVariant,
                                filters: (Object.keys(searchVariant) as SecurityAutocompleteFieldKey[])
                                             .filter(key => key !== SecurityAutocompleteFieldKey.EXCHANGE)
                                             .map<QueryLangFilterCriterion>(key =>
                                                 new QueryLangFilterCriterion({
                                                     fieldNames: [SECURITY_KEY_TO_FIELD_NAME_DICT[key]],
                                                     operator:   QueryLangFilterOperatorExtension.STARTS_WITH,
                                                     query:      smartSecuritySearchVariants[variantIndex][key]
                                                 })
                                             )
                                             .concat([accountExchangesFilter]),
                                isSmart: true
                            }));
                    }

                    return filtersVariants || [];
                })
            );
        }

        return result$ || of([]);
    }

    protected createAsecurityFiltersVariants(
        searchVariants: SecuritySearchVariant[]
    ): Observable<SecuritySearchVariantFilter[]> {
        let filtersVariants: SecuritySearchVariantFilter[];

        const aspreaderExchangeName = EXCHANGE_NAMING[Exchange.AUTOSPREADER];

        const asecuritySearchVariants = searchVariants.filter(filtersVariant =>
            this.isVariantMatchesMainStr(
                filtersVariant,
                SecurityAutocompleteFieldKey.EXCHANGE,
                aspreaderExchangeName
            )
        );

        if (asecuritySearchVariants.length) {
            const searchVariant = asecuritySearchVariants[0];

            filtersVariants = [
                {
                    searchVariant,
                    filters:             [],
                    isAspreader:         true,
                    aspreaderNameSubstr: searchVariant[SecurityAutocompleteFieldKey.SYMBOL]
                }
            ];
        }

        return of(filtersVariants || []);
    }

    protected createExchangeNamingFiltersVariants(
        searchVariants: SecuritySearchVariant[]
    ): Observable<SecuritySearchVariantFilter[]> {
        return this.exchangesService.resolveActiveExchanges().pipe(
            map<Exchange[], SecuritySearchVariantFilter[]>(activeExchanges =>
                searchVariants
                    .filter(searchVariant =>

                        /**
                         * Exclude filters, where symbol should contain spaces (e.g. "b u"),
                         * because it's impossible for common symbols in API (e.g. BTC/USD).
                         */
                        this.isVariantWithoutSpaces(searchVariant, SecurityAutocompleteFieldKey.SYMBOL)
                    )
                    .reduce(
                        (acc, searchVariant) => {
                            const exchangeTerm: string = searchVariant[SecurityAutocompleteFieldKey.EXCHANGE];

                            if (exchangeTerm) {
                                const exchanges: Exchange[] = activeExchanges
                                    .filter((exchange: Exchange) =>
                                        SecurityAutocompleteService.isSubStrIncludedInStrByWords(
                                            EXCHANGE_NAMING[exchange],
                                            exchangeTerm
                                        )
                                    ) as Exchange[];

                                if (exchanges.length) {
                                    const filtersVariant: SecuritySearchVariantFilter = {
                                        searchVariant,
                                        filters: [
                                            new QueryLangFilterCriterion<Security>({
                                                fieldNames: SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
                                                operator:   exchanges.length > 1
                                                                ? QueryLangFilterOperator.IN
                                                                : QueryLangFilterOperator.EQ,
                                                query:      exchanges.join(',')
                                            }),
                                            new QueryLangFilterCriterion<Security>({
                                                fieldNames: SECURITY_SYMBOL_FILTER_FIELD_NAMES,
                                                operator:   QueryLangFilterOperatorExtension.STARTS_WITH,
                                                query:      searchVariant[SecurityAutocompleteFieldKey.SYMBOL]
                                            })
                                        ]
                                    };

                                    acc.push(filtersVariant);
                                }
                            }

                            return acc;
                        },
                        [] as SecuritySearchVariantFilter[]
                    )
            )
        );
    }

    protected prepareFoundSecurities(
        filtersVariant: SecuritySearchVariantFilter,
        securities: Security[],
        doIncludeSmart: boolean
    ): Observable<Security[]> {
        return (filtersVariant.isSmart)
            ? this.getSmartSecuritiesBySecurities(securities)
            : (doIncludeSmart && !filtersVariant.searchVariant.EXCHANGE)
                ? this.getSmartSecuritiesBySecurities(securities).pipe(
                    map(smartSecurities => [...smartSecurities, ...securities])
                )
                : of(securities);
    }

    private isVariantWithoutSpaces(
        searchVariant: SecuritySearchVariant,
        fieldKey: SecurityAutocompleteFieldKey
    ): boolean {
        return !searchVariant[fieldKey] || !searchVariant[fieldKey].includes(' ');
    }

    private isVariantMatchesMainStr(
        searchVariant: SecuritySearchVariant,
        fieldKey: SecurityAutocompleteFieldKey,
        mainStr: string
    ): boolean {
        return !!searchVariant[fieldKey]
            && SecurityAutocompleteService.isSubStrIncludedInStrByWords(mainStr, searchVariant[fieldKey]);
    }

    private getSecurityFieldKeys(): SecurityAutocompleteFieldKey[] {
        const fieldKeys: SecurityAutocompleteFieldKey[] = [
            SecurityAutocompleteFieldKey.SYMBOL
        ];

        if (this.appConfigService.appConfig.displayExchange) {
            fieldKeys.push(SecurityAutocompleteFieldKey.EXCHANGE);
        }

        return fieldKeys;
    }

    private getSmartSecuritiesBySecurities(securities: Security[]): Observable<SmartSecurity[]> {
        const smartSecurityService = this.securityService as SmartSecurityService;

        return smartSecurityService.getSmartBoundExistingAccountsExchanges().pipe(
            map(exchanges => {
                return Object.values(
                    _.groupBy(
                        securities
                            .filter(security => exchanges.includes(security.securityExchange)),
                        (security: Security) => security.symbol
                    )
                )
                    .map((boundSecurities: Security[]) =>
                        smartSecurityService.getOrCreateSmartSecurity(boundSecurities[0], boundSecurities)
                    );
            })
        );
    }

    private findSmartExchangeExclusionFilterIndex(filters: FilterCriterion[]): number {
        return filters.findIndex(filterCriterion => {
            const qlFilter: QueryLangFilterCriterion = filterCriterion as QueryLangFilterCriterion;

            return qlFilter.operator === QueryLangFilterOperator.NE
                && qlFilter.query === Exchange.SMART
                && !!qlFilter.fieldNames
                && qlFilter.fieldNames.length === 1
                && qlFilter.fieldNames[0] === SECURITY_EXCHANGE_FILTER_FIELD_NAMES[0];
        });
    }

}
